import { useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from "react-router-dom";
import styles from "./styles/index.module.css";
import Dashboard from "../../../packages/ui/src/components/Dashboard";
import { MainSendCashScreen } from "../../../packages/ui/src/components/features/send/screens/MainSendCashScreen";
import { MainReceiveScreen } from "../../../packages/ui/src/components/features/receive/screens/MainReceiveScreen";
import { MainBillPayScreen } from "../../../packages/ui/src/components/features/billPay/screens/MainBillPayScreen";
import { MapiMainSendCashScreen } from "../../../packages/ui/src/components/features/mapi-send/screens/MapiMainSendCashScreen";
import { SendMoneyRouter } from "../../../packages/ui/src/components/features/sendmoney/routes/sendMoneyRouter";
import { SEND_MONEY_ROUTES } from "../../../packages/ui/src/components/features/sendmoney/routes/sendMoneyRoutes";
import GlobalLoader from "../../../packages/ui/src/components/uiComponents/GlobalLoader";
import { GlobalErrorProvider }  from '../../../packages/ui/src/components/uiComponents/GlobalErrorProvider';
import {
  useSessionStore,
  scheduleFlowCloseResetForDashboard,
} from "../../../packages/state/src/useSessionStore";
import type { SessionState } from "../../../packages/state/src/useSessionStore";
import { AnalyticsProvider } from "../../../packages/ui/src/components/AnalyticsProvider";
import type { WebFlowRoutingProps } from "../../../packages/ui/src/navigation/webFlowRouting";
import {
  SEND_DEFAULT_SLUG,
  sendSlugToStepKey,
  sendStepKeyToSlug,
  MAPI_SEND_DEFAULT_SLUG,
  mapiSendSlugToStepKey,
  mapiSendStepKeyToSlug,
  RECEIVE_DEFAULT_SLUG,
  receiveSlugToStepKey,
  receiveStepKeyToSlug,
  BILL_PAY_DEFAULT_SLUG,
  billPaySlugToStepKey,
  billPayStepKeyToSlug,
} from "./navigation/flowStepSlugs";

function HomeWithNavigation() {
  const navigate = useNavigate();
  return <Dashboard onServicePress={(service) => {
    console.log('service', service);
    if (service === "send") {
      navigate(`/send/${SEND_DEFAULT_SLUG}`);
    } else if (service === "sendMapi") {
      navigate(`/sendMapi/${MAPI_SEND_DEFAULT_SLUG}`);
    } else if (service === "sendMoney") {
      navigate(SEND_MONEY_ROUTES.PHONE);
    } else if (service === "pickup") {
      navigate(`/receive/${RECEIVE_DEFAULT_SLUG}`)
    } else {
      navigate(`/billPay/${BILL_PAY_DEFAULT_SLUG}`)
    }
  }} />;
}

function useKioskFinishNavigation() {
  const navigate = useNavigate();
  const kioskId = useSessionStore((s: SessionState) => (s as any).kioskId);
  const qrCodeNumber = useSessionStore((s: SessionState) => (s as any).qrCodeNumber);
  const resetSession = useSessionStore((s) => s.reset);
  return useCallback(() => {
    resetSession();
    try {
      const kidFromStore = kioskId && String(kioskId).trim();
      const qrFromStore = qrCodeNumber && String(qrCodeNumber).trim();
      const kidToUse = kidFromStore || qrFromStore;
      if (kidToUse) {
        navigate(`/?t=${encodeURIComponent(kidToUse)}`, { replace: true });
      } else {
        const params = new URLSearchParams(window.location.search);
        const kid = params.get('t');
        if (kid) {
          navigate(`/?t=${encodeURIComponent(kid)}`, { replace: true });
        } else {
          navigate(`/`, { replace: true });
        }
      }
    } catch {
      navigate(`/`, { replace: true });
    }
  }, [navigate, kioskId, qrCodeNumber, resetSession]);
}

function useKioskCloseNavigation() {
  const navigate = useNavigate();
  const kioskId = useSessionStore((s: SessionState) => (s as any).kioskId);
  const qrCodeNumber = useSessionStore((s: SessionState) => (s as any).qrCodeNumber);

  return useCallback(() => {
    try {
      const kidFromStore = kioskId && String(kioskId).trim();
      const qrFromStore = qrCodeNumber && String(qrCodeNumber).trim();
      const params = new URLSearchParams(window.location.search);
      const kidFromUrl = params.get("t");
      const kid = kidFromStore || qrFromStore || kidFromUrl;
      // Defer partial reset until Dashboard mounts (see consumePendingFlowCloseResetIfNeeded)
      // so flow screens unmount first — avoids useWebFlowStepSync rewriting the step slug.
      scheduleFlowCloseResetForDashboard();
      if (kid) {
        navigate(`/?t=${encodeURIComponent(kid)}`, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch {
      scheduleFlowCloseResetForDashboard();
      navigate("/", { replace: true });
    }
  }, [navigate, kioskId, qrCodeNumber]);
}

function MainSendCashScreenNavigation() {
  const navigate = useNavigate();
  const { stepSlug } = useParams();
  const handleFinish = useKioskFinishNavigation();
  const handleClose = useKioskCloseNavigation();

  if (stepSlug != null && stepSlug !== "" && !sendSlugToStepKey(stepSlug)) {
    return <Navigate to={`/send/${SEND_DEFAULT_SLUG}`} replace />;
  }

  const effectiveSlug =
    stepSlug && sendSlugToStepKey(stepSlug) ? stepSlug : SEND_DEFAULT_SLUG;

  const onStepSlugChange = useCallback(
    (next: string) => {
      navigate(`/send/${next}`, { replace: true });
    },
    [navigate]
  );

  const webFlowRouting: WebFlowRoutingProps = useMemo(
    () => ({
      stepSlug: effectiveSlug,
      onStepSlugChange,
      mapStepKeyToSlug: sendStepKeyToSlug,
      mapSlugToStepKey: sendSlugToStepKey,
    }),
    [effectiveSlug, onStepSlugChange]
  );

  return (
    <div className={styles.appFlowWrapper}>
      <MainSendCashScreen
        webFlowRouting={webFlowRouting}
        handleClose={handleClose}
        handleFinish={handleFinish}
      />
    </div>
  );
}


function MainReceiveScreenNavigation() {
  const navigate = useNavigate();
  const { stepSlug } = useParams();
  const handleFinish = useKioskFinishNavigation();
  const handleClose = useKioskCloseNavigation();

  if (stepSlug != null && stepSlug !== "" && !receiveSlugToStepKey(stepSlug)) {
    return <Navigate to={`/receive/${RECEIVE_DEFAULT_SLUG}`} replace />;
  }

  const effectiveSlug =
    stepSlug && receiveSlugToStepKey(stepSlug) ? stepSlug : RECEIVE_DEFAULT_SLUG;

  const onStepSlugChange = useCallback(
    (next: string) => {
      navigate(`/receive/${next}`, { replace: true });
    },
    [navigate]
  );

  const webFlowRouting: WebFlowRoutingProps = useMemo(
    () => ({
      stepSlug: effectiveSlug,
      onStepSlugChange,
      mapStepKeyToSlug: receiveStepKeyToSlug,
      mapSlugToStepKey: receiveSlugToStepKey,
    }),
    [effectiveSlug, onStepSlugChange]
  );

  return (
    <div className={styles.appFlowWrapper}>
      <MainReceiveScreen
        webFlowRouting={webFlowRouting}
        handleClose={handleClose}
        handleFinish={handleFinish}
      />
    </div>
  );
}

function MapiSendCashScreenNavigation() {
  const navigate = useNavigate();
  const { stepSlug } = useParams();
  const kioskId = useSessionStore((s: SessionState) => (s as any).kioskId);
  const resetSession = useSessionStore((s) => s.reset);

  if (stepSlug != null && stepSlug !== "" && !mapiSendSlugToStepKey(stepSlug)) {
    return <Navigate to={`/sendMapi/${MAPI_SEND_DEFAULT_SLUG}`} replace />;
  }

  const effectiveSlug =
    stepSlug && mapiSendSlugToStepKey(stepSlug) ? stepSlug : MAPI_SEND_DEFAULT_SLUG;

  const onStepSlugChange = useCallback(
    (next: string) => {
      navigate(`/sendMapi/${next}`, { replace: true });
    },
    [navigate]
  );

  const webFlowRouting: WebFlowRoutingProps = useMemo(
    () => ({
      stepSlug: effectiveSlug,
      onStepSlugChange,
      mapStepKeyToSlug: mapiSendStepKeyToSlug,
      mapSlugToStepKey: mapiSendSlugToStepKey,
    }),
    [effectiveSlug, onStepSlugChange]
  );

  const handleFinish = useCallback(() => {
    resetSession();
    try {
      const kidFromStore = kioskId && String(kioskId).trim();
      if (kidFromStore) {
        navigate(`/?t=${encodeURIComponent(kidFromStore)}`);
      } else {
        const params = new URLSearchParams(window.location.search);
        const kid = params.get("t");
        if (kid) {
          navigate(`/?t=${encodeURIComponent(kid)}`);
        } else {
          navigate(`/`);
        }
      }
    } catch {
      navigate(`/`);
    }
  }, [navigate, kioskId, resetSession]);

  return (
    <div className={styles.appFlowWrapper}>
      <MapiMainSendCashScreen
        webFlowRouting={webFlowRouting}
        handleClose={() => {
          navigate("/");
        }}
        handleFinish={handleFinish}
      />
    </div>
  );
}

function MainBillPayScreenNavigation() {
  const navigate = useNavigate();
  const { stepSlug } = useParams();
  const handleFinish = useKioskFinishNavigation();
  const handleClose = useKioskCloseNavigation();

  if (stepSlug != null && stepSlug !== "" && !billPaySlugToStepKey(stepSlug)) {
    return <Navigate to={`/billPay/${BILL_PAY_DEFAULT_SLUG}`} replace />;
  }

  const effectiveSlug =
    stepSlug && billPaySlugToStepKey(stepSlug) ? stepSlug : BILL_PAY_DEFAULT_SLUG;

  const onStepSlugChange = useCallback(
    (next: string) => {
      navigate(`/billPay/${next}`, { replace: true });
    },
    [navigate]
  );

  const webFlowRouting: WebFlowRoutingProps = useMemo(
    () => ({
      stepSlug: effectiveSlug,
      onStepSlugChange,
      mapStepKeyToSlug: billPayStepKeyToSlug,
      mapSlugToStepKey: billPaySlugToStepKey,
    }),
    [effectiveSlug, onStepSlugChange]
  );

  return (
    <div className={styles.appFlowWrapper}>
      <MainBillPayScreen
        webFlowRouting={webFlowRouting}
        handleClose={handleClose}
        handleFinish={handleFinish}
      />
    </div>
  );
}

export default function App() {
  return (
    <AnalyticsProvider>
    <BrowserRouter>
      <GlobalErrorProvider> 
        <Routes>
          <Route path="/" element={<HomeWithNavigation />} />
          <Route path="/send" element={<Navigate to={`/send/${SEND_DEFAULT_SLUG}`} replace />} />
          <Route path="/send/:stepSlug" element={<MainSendCashScreenNavigation />} />
          <Route path="/sendMapi" element={<Navigate to={`/sendMapi/${MAPI_SEND_DEFAULT_SLUG}`} replace />} />
          <Route path="/sendMapi/:stepSlug" element={<MapiSendCashScreenNavigation />} />
          <Route path="/receive" element={<Navigate to={`/receive/${RECEIVE_DEFAULT_SLUG}`} replace />} />
          <Route path="/receive/:stepSlug" element={<MainReceiveScreenNavigation />} />
          <Route path="/billPay" element={<Navigate to={`/billPay/${BILL_PAY_DEFAULT_SLUG}`} replace />} />
          <Route path="/billPay/:stepSlug" element={<MainBillPayScreenNavigation />} />
          <Route path="/sendMoney" element={<Navigate to={SEND_MONEY_ROUTES.PHONE} replace />} />
          <Route path="/sendMoney/*" element={
            <div className={styles.appFlowWrapper}>
              <SendMoneyRouter />
            </div>
          } />
        </Routes>
        <GlobalLoader />
      </GlobalErrorProvider> 
    </BrowserRouter>
    </AnalyticsProvider>
  );
}
